import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme, Box, Typography } from '@mui/material';

const StackedBarChart = ({ 
  data = [], 
  title = "",  
  bottomLegend, 
  leftLegend, 
  digit = 1,
  isEnableTotals = true, 
  isHorizontalBottomLegend, 
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0, 
  translateXOffSet = 0,
  translateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {
  const theme = useTheme();
  const neutralDark = theme.palette.neutral.dark;
  const textPrimaryColor = theme.palette.text.primary;
  
  if (!data || data.length === 0 || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  const formatData = (data, digit = 1) => {
    return data.map(row => {
      const formattedRow = { x: row.x };
      for (const [key, val] of Object.entries(row)) {
        if (key !== 'x') {
          formattedRow[key] = val ? Number(val.toFixed(digit)) : 0;
        }
      }
      return formattedRow;
    });
  }

  function normalizeData(data) {
    // 1. lấy tất cả keys xuất hiện
    const allKeys = Array.from(
      new Set(data.flatMap(row => Object.keys(row).filter(k => k !== "x")))
    );

    // 2. map lại mỗi row, điền 0 cho key thiếu
    return data.map(row => {
      const normalized = { x: row.x };
      for (const key of allKeys) {
        normalized[key] = row[key] || 0;
      }
      return normalized;
    });
  }

  const formattedData = formatData(normalizeData(data), digit);

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      gap={1} 
      sx={{ height: '100%', width: '100%' }}
    >
      {title && 
        <Typography 
          variant="h6" 
          align="center"
        >
          {title}
        </Typography>
      }

      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsiveBar
          data={formattedData}        
          keys={Object.keys(formattedData[0]).filter(k => k !== 'x' && typeof formattedData[0][k] === 'number')}
          indexBy={`x`}
          margin={{ 
            top: 35 + (topOffSet || 0),
            right: 20, 
            bottom: 155 + (bottomOffSet || 0), 
            left: 80 
          }}
          padding={0.3}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          // colors={{ scheme: 'category10' }}
          colors={({ id }) => {
            const palette = ["#42d442ff", "#43a2f0ff", "#FFC107", "#dc5d8cff", "#c98134ff", "#aec934ff", "#16d4d0ff", "#c934c9ff"];
            const keys = Object.keys(formattedData[0]).filter(k => k !== Object.keys(formattedData[0])[0]);
            const idx = keys.indexOf(id);
            return palette[idx % palette.length];
          }}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          borderColor={{ from: 'color', modifiers: [['darker', 1.5]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 45 + bottomLegendOffSet,
            tickRotation: isHorizontalBottomLegend ? 0 : 270,
            legend: bottomLegend,
            legendPosition: 'middle',
            legendOffset: 75 + bottomLegendOffSet,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: leftLegend || 'Value',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          enableTotals={isEnableTotals}
          totalsOffset={8}
          enableLabel={true} 
          labelTextColor={{ from: 'color', modifiers: [['brighter', 5.0]] }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelPosition="middle"
          labelOffset={0}
          animate={true}
          motionStiffness={90}
          motionDamping={15}  
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: 10 + translateXOffSet,
              translateY: -40 + translateYOffSet,
              itemsSpacing: 10 + itemsSpacingOffSet,
              itemWidth: 80 + itemWidthOffSet,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.95,
              symbolSize: 12,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: neutralDark,
                },
              },
              legend: {
                text: {
                  fill: textPrimaryColor,
                },
              },
              ticks: {
                line: {
                  stroke: textPrimaryColor,
                  strokeWidth: 1,
                },
                text: {
                  fill: textPrimaryColor,
                },
              },
            },  
            legends: {
              text: {
                fill: textPrimaryColor,
              },
            },        
            grid: {
              line: {
                stroke: neutralDark,
                strokeWidth: 0.2,
                strokeDasharray: '2 2'
              }
            },
            tooltip: {
              container: {
                background: theme.palette.neutral.light,
                color: theme.palette.text.primary,
                borderRadius: '4px',
                padding: '8px',
              },
            },
            labels: {
              text: {
                fill: textPrimaryColor,
                fontSize: '12px' 
              },
            },
            dots: {
              text: {
                fill: textPrimaryColor,
              },
            },
          }}      
        />
      </Box>
    </Box>
  );
};

export default StackedBarChart;
